/**
 * Administração › Usuários › ficha: dados, acesso, ações da conta e auditoria — Nexora Grid (frontend, US-1804).
 *
 * Criado em: 19/10/2026 · Última modificação: 02/11/2026 · Versão: 2.0.0
 *
 * Ações com confirmação em dois passos (o botão vira "Confirmar …"): desativar (encerra a sessão), reativar,
 * desbloquear, encerrar sessão, reenviar convite e enviar link de nova senha. A própria conta não se altera aqui.
 * Edição com a versão lida: se outra pessoa alterou antes, a tela avisa e recarrega.
 * v2.0 (US-4604, Sprint 46): padrão de ficha do guia (§4.2) — cabeçalho com migalhas, situação e "Voltar" para a lista
 *   como a pessoa deixou; abas Dados (com a escala de turnos), Acesso (horário, sessão, bloqueio) e Auditoria (linha do
 *   tempo: quem, quando, o quê); ao lado, as ações da conta e a atividade recente. Mesmas chamadas e as mesmas regras.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, ErroApi } from "../api";
import { Abas, CabecalhoTela, ErroCarregar, Esqueleto, Selo, useParametro } from "../componentes/Tela";
import { idadeRelativa } from "../componentes/regrasTela";
import { NOMES } from "../nomes";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { EscalaTurnos } from "./EscalaTurnos";
import { corpoDoForm, FormUsuario, type DadosForm } from "./FormUsuario";
import { descreverJanela, EVENTOS, NOME_PAPEL, PERFIS, quando, situacao, type Evento, type UsuarioAdm } from "./tipos";
import "./admin-b.css";

type Acao = { rota: string; texto: string; confirmar: string; visivel: (u: UsuarioAdm) => boolean };
const ACOES: Acao[] = [
  { rota: "desativar", texto: "Desativar", confirmar: "Confirmar desativação", visivel: (u) => u.ativo },
  { rota: "reativar", texto: "Reativar", confirmar: "Confirmar reativação", visivel: (u) => !u.ativo },
  { rota: "desbloquear", texto: "Desbloquear", confirmar: "Confirmar desbloqueio", visivel: (u) => u.bloqueado },
  { rota: "encerrar-sessao", texto: "Encerrar sessão", confirmar: "Confirmar encerramento", visivel: (u) => u.em_sessao },
  { rota: "reenviar-convite", texto: "Reenviar convite", confirmar: "Confirmar novo convite", visivel: (u) => !u.email_confirmado && !!u.email && u.ativo },
  { rota: "redefinir-senha", texto: "Enviar link de nova senha", confirmar: "Confirmar envio", visivel: (u) => !!u.email && u.ativo },
];
type Aba = "dados" | "acesso" | "auditoria";
const ABAS: { chave: Aba; texto: string }[] = [{ chave: "dados", texto: "Dados" }, { chave: "acesso", texto: "Acesso" }, { chave: "auditoria", texto: "Auditoria" }];
const MIGALHAS_BASE = [{ texto: NOMES.administracao }, { texto: NOMES.usuarios, para: "/admin/usuarios" }];

function paraForm(u: UsuarioAdm): DadosForm {
  return { login: u.login, nome: u.nome, email: u.email ?? "", perfil: u.perfil,
    empresa: u.empresa ?? "", papeis: u.papeis, empresas_atribuidas: u.empresas_atribuidas.join(", "),
    usarJanela: !!u.janela_acesso, janela: u.janela_acesso ?? { dias: [0, 1, 2, 3, 4], inicio: "07:00", fim: "19:00" } };
}

function ItemEvento({ e }: { e: Evento }) {
  const d = lerDetalhe(e.detalhe);
  return (
    <li>
      <b>{EVENTOS[e.evento] ?? e.evento}</b>
      <small><span className="tl-mono">{quando(e.instante)}</span>{d ? ` · ${d}` : ""}{e.ip ? ` · ${e.ip}` : ""}</small>
    </li>
  );
}

export function UsuarioDetalhe() {
  const { id } = useParams();
  const [parametros] = useSearchParams();
  const { usuario: ator } = useSessao();
  const [u, setU] = useState<UsuarioAdm | null>(null);
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(parametros.get("criado") ? "Usuário criado. O convite está a caminho do e-mail informado." : null);
  const [editando, setEditando] = useState(parametros.get("editar") === "1");
  const [enviando, setEnviando] = useState(false);
  const [confirmando, setConfirmando] = useState<string | null>(null);
  const [aba, setAba] = useParametro("aba", "dados");
  const consulta = useRef(0);

  const carregar = useCallback(() => {
    if (!id) return;
    const atual = ++consulta.current;
    api<UsuarioAdm>(`/v1/usuarios/${id}`).then((dados) => { if (consulta.current === atual) { setU(dados); setErro(null); } })
      .catch((e) => { if (consulta.current === atual) setErro(e instanceof ErroApi && e.status === 404 ? "Usuário não encontrado." : String(e.message ?? e)); });
    api<Evento[]>(`/v1/usuarios/${id}/auditoria?limite=50`).then((dados) => { if (consulta.current === atual) setEventos(dados); })
      .catch(() => { if (consulta.current === atual) setEventos([]); });
  }, [id, ator]);
  useEffect(() => {
    carregar();
    const intervalo = window.setInterval(carregar, 30000);
    window.addEventListener("focus", carregar);
    return () => { consulta.current++; window.clearInterval(intervalo); window.removeEventListener("focus", carregar); };
  }, [carregar]);

  if (!ator) return null;
  if (erro) return (
    <section className="tl-pagina admin-b">
      <CabecalhoTela voltar="/admin/usuarios" migalhas={[...MIGALHAS_BASE, { texto: "Ficha" }]} titulo={NOMES.usuarios} />
      <ErroCarregar mensagem={erro} aoTentar={carregar} />
    </section>
  );
  if (!u || u.id !== id) return <section className="tl-pagina admin-b"><Esqueleto linhas={8} /></section>;
  if (u.perfil === "cliente" ? !u.empresa || !pode.verEmpresa(ator, u.empresa) : ator.perfil !== "integrador")
    return <p className="nx-suave">Usuário não encontrado.</p>;
  const propria = u.id === ator.id;
  // a tela só esconde; o backend confere (conta da equipe do integrador: só o gestor do sistema — US-1805)
  const gerencia = u.pode_gerenciar === true && pode.gerenciarUsuarios(ator)
    && (u.perfil !== "integrador" || (ator.funcoes_treinamento ?? []).includes("gestor_sistema"));
  const s = situacao(u);
  const abaAtiva = (ABAS.some((a) => a.chave === aba) ? aba : "dados") as Aba;

  const salvar = async (d: DadosForm) => {
    setEnviando(true);
    try {
      setU({ ...await api<UsuarioAdm>(`/v1/usuarios/${u.id}`, { metodo: "PUT", corpo: { versao: u.versao, ...corpoDoForm(d, false) } }), pode_gerenciar: u.pode_gerenciar });
      setEditando(false);
      setAviso("Alterações salvas.");
      carregar();
    } catch (e) {
      if (e instanceof ErroApi && e.status === 409) { setAviso("Outra pessoa alterou este usuário antes. Os dados foram recarregados."); setEditando(false); carregar(); }
      else setAviso(e instanceof ErroApi ? `Não foi possível salvar: ${e.message}` : "Falha de rede.");
    } finally { setEnviando(false); }
  };

  const executar = async (a: Acao) => {
    if (confirmando !== a.rota) { setConfirmando(a.rota); return; }
    setConfirmando(null);
    try {
      await api(`/v1/usuarios/${u.id}/${a.rota}`, { corpo: {} });
      setAviso(`${a.texto}: feito.`);
      carregar();
    } catch (e) { setAviso(e instanceof ErroApi ? `Não foi possível: ${e.message}` : "Falha de rede."); }
  };
  const acoes = ACOES.filter((a) => a.visivel(u));

  return (
    <section className="tl-pagina admin-b">
      <CabecalhoTela voltar="/admin/usuarios" migalhas={[...MIGALHAS_BASE, { texto: u.nome }]} titulo={u.nome}
        subtitulo={<span className="tl-mono">{u.login}</span>}
        selos={<span className="linha-titulo selos-cab"><span className={`nx-selo ${s.classe}`}>{s.texto}</span>
          {u.em_sessao && <Selo>em sessão agora</Selo>}<Selo tom="suave">{PERFIS[u.perfil]}</Selo></span>}
        acoes={gerencia && !editando ? <button type="button" className="nx-botao nx-botao-principal" onClick={() => setEditando(true)}>Editar</button> : undefined} />
      {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
      {propria && <p className="tl-texto">Esta é a sua conta: alterações nela são feitas por outra pessoa com permissão.</p>}
      {editando && gerencia ? (
        <FormUsuario ator={ator} inicial={paraForm(u)} novo={false} enviando={enviando} textoBotao="Salvar alterações"
          aoEnviar={(d) => void salvar(d)} aoCancelar={() => setEditando(false)} />
      ) : (
        <div className="ficha-b">
          <div className="tl-bloco">
            <Abas rotulo="Partes da ficha do usuário" abas={ABAS} ativa={abaAtiva} aoMudar={setAba} />
            <div className="tl-painel-aba" role="tabpanel">
              {abaAtiva === "dados" && (
                <>
                  <dl className="ficha-usuario tl-kv">
                    <dt>Login</dt><dd className="nx-valor">{u.login}</dd>
                    <dt>E-mail</dt><dd>{u.email ?? "—"} {u.email && <small className="nx-suave">({u.email_confirmado ? "confirmado" : "não confirmado"})</small>}</dd>
                    <dt>Perfil</dt><dd>{PERFIS[u.perfil]}</dd>
                    {u.perfil === "cliente" && <><dt>Operadora</dt><dd>{u.empresa}</dd><dt>Papéis</dt><dd>{u.papeis.map((p) => NOME_PAPEL[p] ?? p).join(", ")}</dd></>}
                    {u.perfil === "suporte" && <><dt>Operadoras atribuídas</dt><dd>{u.empresas_atribuidas.join(", ")}</dd></>}
                    <dt>Criado em</dt><dd>{quando(u.criado_em)}</dd>
                    <dt>Versão</dt><dd>{u.versao}</dd>
                  </dl>
                  {u.perfil === "cliente" && <EscalaTurnos usuarioId={u.id} edita={gerencia} />}
                </>
              )}
              {abaAtiva === "acesso" && (
                <dl className="tl-kv">
                  <dt>Horário de acesso</dt><dd>{descreverJanela(u.janela_acesso)}</dd>
                  <dt>Último acesso</dt><dd>{u.ultimo_login ? <>{quando(u.ultimo_login)} <small className="nx-suave">({idadeRelativa(u.ultimo_login)})</small></> : "nunca entrou"}</dd>
                  <dt>Sessão</dt><dd>{u.em_sessao ? "em sessão agora" : "sem sessão aberta"}</dd>
                  <dt>Bloqueio</dt><dd>{u.bloqueado ? `bloqueado até ${quando(u.bloqueado_ate)}` : "sem bloqueio"}</dd>
                  <dt>Conta</dt><dd>{u.ativo ? "ativa" : "desativada"}</dd>
                  <dt>E-mail</dt><dd>{u.email ? (u.email_confirmado ? "confirmado" : "não confirmado") : "sem e-mail"}</dd>
                </dl>
              )}
              {abaAtiva === "auditoria" && (
                eventos.length ? (
                  <ol className="tl-linha-tempo" aria-label="Histórico de acesso e de alterações">
                    {eventos.map((e, i) => <ItemEvento key={i} e={e} />)}
                  </ol>
                ) : <p className="tl-texto">sem eventos</p>
              )}
            </div>
          </div>
          <aside className="ficha-b-lado">
            {gerencia && acoes.length > 0 && (
              <section className="tl-bloco">
                <div className="tl-bloco-cab"><h3>Ações da conta</h3></div>
                <div className="tl-bloco-corpo">
                  <div className="acoes-conta" aria-label="Ações da conta">
                    {acoes.map((a) => (
                      <button key={a.rota} type="button" className={`nx-botao${confirmando === a.rota ? " nx-botao-principal" : ""}`}
                        onClick={() => void executar(a)} onBlur={() => setConfirmando((c) => (c === a.rota ? null : c))}>
                        {confirmando === a.rota ? a.confirmar : a.texto}
                      </button>
                    ))}
                  </div>
                  <small className="nx-suave">Cada ação pede um segundo clique para confirmar.</small>
                </div>
              </section>
            )}
            <section className="tl-bloco">
              <div className="tl-bloco-cab"><h3>Histórico de acesso e de alterações</h3></div>
              <div className="tl-bloco-corpo">
                <ul className="auditoria tl-linha-tempo">
                  {eventos.slice(0, 6).map((e, i) => <ItemEvento key={i} e={e} />)}
                  {!eventos.length && <li className="nx-suave">sem eventos</li>}
                </ul>
                {eventos.length > 6 && <button type="button" className="tl-fantasma" onClick={() => setAba("auditoria")}>Ver os {eventos.length} eventos</button>}
              </div>
            </section>
          </aside>
        </div>
      )}
    </section>
  );
}

/** Detalhe da auditoria em texto curto (quem fez e o que mudou). */
function lerDetalhe(detalhe: string): string {
  if (!detalhe) return "";
  try {
    const d = JSON.parse(detalhe) as { por?: string; motivo?: string; mudancas?: Record<string, unknown> };
    const campos = d.mudancas ? ` (${Object.keys(d.mudancas).join(", ")})` : "";
    return `por ${d.por ?? "—"}${campos}${d.motivo ? ` — ${d.motivo}` : ""}`;
  } catch {
    return detalhe;
  }
}
